import {Component, OnDestroy} from '@angular/core';

import {LatLng, Map} from 'leaflet';
import { Subscription, switchMap } from 'rxjs';

import {MapService} from '@commons/services/map/map/map.service';
import {MinimapService} from "@src/app/map/services/minimap.service";
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { NewWebSocketHttpService } from '@shared/websocket/new-web-socket-http.service';
import { Vehicle } from '@commons/models/domain/Vehicle';
import { ActivatedRoute } from '@angular/router';
import { VehicleControlIndicator } from '@commons/models/domain/VehicleControlIndicator';
import { StatusTimeFormatPipe } from '@commons/pipes/status-time-format.pipe';
import { TranslateService } from '@ngx-translate/core';
import { FullAddressPipe } from '@commons/pipes/full-address.pipe';
import { CountryCodeService } from '@commons/services/country.code/country-code.service';

@Component({
  selector: 'tk-map-main-layer',
  templateUrl: './map-main-layer.component.html',
  providers: [StatusTimeFormatPipe]
})
export class MapMainLayerComponent implements OnDestroy {

  /**
   *  Ustawienia mapy (zbindowane do głownej wartwy mapy)
   */
  options = {
    layers: [],
    zoom: 12,                  // zoom mapy na start
    zoomControl: false,        // wyłączenie przycisków zoom
    attributionControl: false, // wyłączenie linku w prawym dolnym rogu
    center: new LatLng(        // centrum mapy na start
      51.403844,
      21.158246
    ),
    closePopupOnClick: false,
    minZoom: 1,
  };

  private currentSelectedVehicle: number;
  private currentSelectedVehicleButtonCoords: Element;
  private $fullAddressPipe = new FullAddressPipe();
  
  private onChangeVehicleDataSubsc: Subscription;
  private readonly ComponentSubsc: Subscription = new Subscription();
  private readonly PopupSubsc: Subscription = new Subscription();

  constructor(
    private mapService: MapService,
    private minimapService: MinimapService,
    private vehicleService: VehicleService,
    private newWebSocketHttpService: NewWebSocketHttpService,
    private activatedRoute: ActivatedRoute,
    private statusTimeFormatPipe: StatusTimeFormatPipe,
    private translateService: TranslateService,
    private countryCodeService: CountryCodeService
  ) {
  }

  onMapReady(map: Map): void {
    // Delegacja + podłączenie mapy do serwisu
    this.mapService.onMapReady(map);
    this.minimapService.onMapReady(map);
    this.onChangeSelectedVehicle();
  }

  ngOnDestroy(): void {
    /**
     * Gdy główny komponent mapy jest niszczony trzeba odsubskrybować
     * wszystkie strumienie zasubkrybowane przez jej serwis
     */
    this.mapService.unsubscribeAllStreams();
    this.ComponentSubsc.unsubscribe();
    this.onChangeVehicleDataSubsc?.unsubscribe();
    this.PopupSubsc.unsubscribe();
  }

  // aktualizowanie danych w popupie markera
  private onChangeSelectedVehicle(): void {
    const sub = this.activatedRoute.queryParams.subscribe(
      (params) => {
        const { id, mode } = params;

        if ((!id && this.currentSelectedVehicle) || (this.currentSelectedVehicle && Number(id) !== this.currentSelectedVehicle)) {
          this.hideDetailsPopupSelectedVehicle();
        }
        if (id && mode === 'details') {
          this.currentSelectedVehicle = Number(id);
          this.showDetailsPopupSelectedVehicle(this.currentSelectedVehicle);
          this.onChangeVehicleData(this.currentSelectedVehicle);
        }
      }
    );
    this.ComponentSubsc.add(sub);
  }

  private onChangeVehicleData(idVehicle: number): void {
    this.onChangeVehicleDataSubsc = this.newWebSocketHttpService.updatedVehicles
      .pipe(
        switchMap(() => this.vehicleService.getDetails$(idVehicle)),
      )
      .subscribe(
        (dataVehicle) => {
          this.updateVehicleDataPopupMarker(dataVehicle);
        }
      ); 
  }

  private updateVehicleDataPopupMarker(data: Vehicle): void {    
    const streetElement = this.getSelectedVehiclePopup(data.Id)?.querySelector('.details-vehicle__street');
    const dateElement = this.getSelectedVehiclePopup(data.Id)?.querySelector('.details-vehicle__date');

    this.popupSelectedVehicleListeners();

    if (streetElement && data.LL) {
      data.LL.Cr = this.countryCodeService.getCode(data.LL.Cr);
      const streetValue = this.$fullAddressPipe.transform(data.LL);
      streetElement.textContent = streetValue;
      streetElement.setAttribute('title', streetValue);
  
      if (dateElement) {
        try {
          const paramPosition: VehicleControlIndicator | undefined = data.ControlIndicators?.find(x => x.Tp == 22);

          if (paramPosition) {
            const lastPositionTime: Date = new Date(paramPosition.LV.T);
            const n = new Date();
            const date = Math.ceil((n.getTime() - lastPositionTime.getTime()) / 1000);

            if (date) {
              dateElement.textContent = this.statusTimeFormatPipe.transform(date) + ' ' + this.translateService.instant('labels.ago');
            }
          }
        }
        catch (_) {
          dateElement.textContent = 'Brak daty ostatniej aktualizacji';
        }
      }
    }
  }

  private getSelectedVehiclePopup(id: number): Element | null {
    const elements = Array.from(document.getElementsByClassName(`popup-vehicle-${id}`));
    return elements.length > 0 ? elements[0] : null;
  }

  private showDetailsPopupSelectedVehicle(id: number): void {
    this.getSelectedVehiclePopup(id)?.classList.add('is-selected');
  }

  private hideDetailsPopupSelectedVehicle(id = this.currentSelectedVehicle): void {
    this.onChangeVehicleDataSubsc?.unsubscribe();
    this.PopupSubsc.unsubscribe();
    this.currentSelectedVehicleButtonCoords.removeEventListener('click', () => {});
    this.getSelectedVehiclePopup(id)?.classList.remove('is-selected');
    this.currentSelectedVehicle = null as any;
  }

  private popupSelectedVehicleListeners(): void {
    const popup = this.getSelectedVehiclePopup(this.currentSelectedVehicle);
    const buttonCopyCoords = popup?.querySelector('#button-copy-coords');

    if (buttonCopyCoords) {
      setTimeout(() => {
        this.currentSelectedVehicleButtonCoords = buttonCopyCoords;
        this.currentSelectedVehicleButtonCoords.addEventListener('click', () => {
         navigator.clipboard.writeText(buttonCopyCoords.getAttribute('data-vehicle-coords') as string);
       });
      }, 0);
    }
  }

}
